import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Row, Button, Container, Badge } from 'shards-react';
import {
  Table,
  THead,
  Th,
  Td,
  TableRow,
  TableHeader,
  TableWrapper,
  H3
} from '@increase/typed-components';
import PageTitle from '../../../components/PageTitle';
import Errors from '../../../components/Errors';
import CredentialMigrationService from '../../../services/CredentialMigrationService';
import { Can } from '../../../permissions/helper';

const STATUS_THEME = {
  success: 'success',
  failed: 'danger',
  pending: 'secondary',
  completed: 'success',
  partially_failed: 'warning',
  api_failed: 'danger'
};

const STEPS = [
  { key: 'apiStep', label: 'API (atómico)' },
  { key: 'scraperStep', label: 'Scraper' },
  { key: 'schedulerStep', label: 'Scheduler' }
];

export function MigrationStatus({ migrationId }) {
  const [migration, setMigration] = useState(null);
  const [errors, setErrors] = useState([]);

  const fetchMigration = useCallback(() => {
    const credentialMigrationService = CredentialMigrationService();
    credentialMigrationService
      .get(migrationId)
      .then((data) => {
        if (!data) {
          setErrors(['No se encontró la migración']);
          return;
        }
        setMigration(data);
      })
      .catch(() => setErrors(['No se pudo cargar la migración']));
  }, [migrationId]);

  useEffect(() => {
    fetchMigration();
  }, [fetchMigration]);

  // Keeps refreshing while Scraper/Scheduler propagation is still running.
  const inProgress = migration && ['pending', 'partially_failed'].includes(migration.status);
  useEffect(() => {
    if (!inProgress) return undefined;
    const interval = setInterval(fetchMigration, 5000);
    return () => clearInterval(interval);
  }, [inProgress, fetchMigration]);

  const onRetry = (step) => {
    setErrors([]);
    const credentialMigrationService = CredentialMigrationService();
    credentialMigrationService
      .retry(migrationId, [step])
      .then((data) => {
        if ((data.errors || []).length > 0) {
          setErrors(data.errors);
          return;
        }
        fetchMigration();
      })
      .catch(() => setErrors(['No se pudo reintentar el paso']));
  };

  if (!migration) {
    return (
      <Container className="main-content-container px-4" fluid>
        <Errors errors={errors} />
      </Container>
    );
  }

  return (
    <Container className="main-content-container px-4" fluid>
      <Row className="page-header py-4" noGutters>
        <PageTitle className="text-sm-left" sm="4" subtitle="" title="Estado de migración" />
      </Row>
      <Errors errors={errors} />
      <p>
        <strong>Credencial:</strong> {migration.credentialId} ({migration.infoProvider})
      </p>
      <p>
        <strong>Cuenta origen:</strong> {migration.fromAccountId}
      </p>
      <p>
        <strong>Cuenta destino:</strong> {migration.toAccountId}
      </p>
      <p>
        <strong>Estado general:</strong>{' '}
        <Badge pill theme={STATUS_THEME[migration.status] || 'secondary'}>
          {migration.status}
        </Badge>
      </p>
      <TableWrapper className="MarginTop20">
        <TableHeader>
          <H3>Pasos</H3>
        </TableHeader>
        <Table hasData zebra>
          <THead>
            <tr>
              <Th>Paso</Th>
              <Th>Estado</Th>
              <Th>Error</Th>
              <Th>Acción</Th>
            </tr>
          </THead>
          <tbody>
            {STEPS.map(({ key, label }) => {
              const step = migration[key];
              const failed = step.retryable;
              const stepName = key.replace('Step', '').toLowerCase();
              return (
                <TableRow key={key}>
                  <Td>{label}</Td>
                  <Td>
                    <Badge pill theme={STATUS_THEME[step.status] || 'secondary'}>
                      {step.status}
                    </Badge>
                  </Td>
                  <Td>{step.error || '-'}</Td>
                  <Td>
                    {failed && stepName !== 'api' && (
                      <Can action="cred:edit:acc">
                        <Button onClick={() => onRetry(stepName)} size="sm">
                          Reintentar
                        </Button>
                      </Can>
                    )}
                  </Td>
                </TableRow>
              );
            })}
          </tbody>
        </Table>
      </TableWrapper>
      <TableWrapper className="MarginTop20">
        <TableHeader>
          <H3>Historial de eventos</H3>
        </TableHeader>
        <Table hasData={migration.events.length > 0} noDataMessage="No hay eventos" zebra>
          <THead>
            <tr>
              <Th>Fecha</Th>
              <Th>Paso</Th>
              <Th>Estado</Th>
              <Th>Error</Th>
            </tr>
          </THead>
          <tbody>
            {migration.events.map((event, index) => (
              <TableRow key={index}>
                <Td>{new Date(event.createdAt).toLocaleString()}</Td>
                <Td>{event.step}</Td>
                <Td>
                  <Badge pill theme={STATUS_THEME[event.status] || 'secondary'}>
                    {event.status}
                  </Badge>
                </Td>
                <Td>{event.errorMessage || '-'}</Td>
              </TableRow>
            ))}
          </tbody>
        </Table>
      </TableWrapper>
    </Container>
  );
}

MigrationStatus.propTypes = {
  migrationId: PropTypes.string.isRequired
};
